import { EffectCarousel } from './theme.js';
import { timeline } from './vendor.min.js';

/**
 * Vertical Swiper
 *
 * Rotates the section's top-level blocks vertically inside a viewport whose
 * height is owned by the section, so slide content never resizes the wrapper.
 * Extends the theme's EffectCarousel (same base as the announcement bar) and
 * only replaces the transition.
 *
 * Attributes:
 * @attr {string} effect - slide | fade | flip | none
 * @attr {number} speed - transition duration in ms
 * @attr {number} [autoplay] - delay in seconds, set only when autoplay is on
 */
class VerticalSwiper extends EffectCarousel {
  static DEFAULT_SPEED = 400;

  get effect() {
    return this.getAttribute('effect') || 'slide';
  }

  /** Motion expects seconds, the setting is authored in milliseconds. */
  get transitionDuration() {
    const speed = parseInt(this.getAttribute('speed'), 10);

    return (Number.isNaN(speed) ? VerticalSwiper.DEFAULT_SPEED : speed) / 1000;
  }

  /**
   * Swiping stays off: the component is typically a thin bar, where a drag
   * gesture competes with page scrolling and with links inside the slides.
   */
  get swipeable() {
    return false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.player?.stop();
  }

  _transitionTo(fromSlide, toSlide, { direction = 'next', animate = true } = {}) {
    fromSlide.classList.remove('is-selected');
    toSlide.classList.add('is-selected');

    const [fromKeyframes, toKeyframes] = this._keyframesFor(direction);
    const isAnimated = animate && this.effect !== 'none';

    return timeline(
      [
        [fromSlide, fromKeyframes, { zIndex: { easing: 'step-end' } }],
        [toSlide, toKeyframes, { at: '<', zIndex: { easing: 'step-end' } }],
      ],
      { duration: isAnimated ? this.transitionDuration : 0 }
    ).finished;
  }

  /**
   * Keyframes for the outgoing and incoming slide.
   *
   * Motion is direction aware so a looping carousel never plays backwards when
   * it wraps from the last slide to the first one.
   *
   * @param {string} direction - 'next' or 'previous'
   * @returns {Array} A pair of keyframe objects: [fromKeyframes, toKeyframes]
   */
  _keyframesFor(direction) {
    const isForward = direction !== 'previous';

    switch (this.effect) {
      case 'slide': {
        const exit = isForward ? '-100%' : '100%';
        const enter = isForward ? '100%' : '-100%';

        return [
          {
            transform: ['translateY(0)', `translateY(${exit})`],
            opacity: [1, 0],
            visibility: ['visible', 'hidden'],
            zIndex: 0,
          },
          {
            transform: [`translateY(${enter})`, 'translateY(0)'],
            opacity: [0, 1],
            visibility: ['hidden', 'visible'],
            zIndex: 1,
          },
        ];
      }

      case 'flip': {
        const exit = isForward ? '-90deg' : '90deg';
        const enter = isForward ? '90deg' : '-90deg';

        return [
          {
            transform: ['rotateX(0deg)', `rotateX(${exit})`],
            opacity: [1, 0],
            visibility: ['visible', 'hidden'],
            zIndex: 0,
          },
          {
            transform: [`rotateX(${enter})`, 'rotateX(0deg)'],
            opacity: [0, 1],
            visibility: ['hidden', 'visible'],
            zIndex: 1,
          },
        ];
      }

      default: {
        // `fade` and `none` share keyframes, `none` simply runs with a 0s duration.
        return [
          { opacity: [1, 0], visibility: ['visible', 'hidden'], zIndex: 0 },
          { opacity: [0, 1], visibility: ['hidden', 'visible'], zIndex: 1 },
        ];
      }
    }
  }
}

if (!window.customElements.get('vertical-swiper')) {
  window.customElements.define('vertical-swiper', VerticalSwiper);
}
